import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import { addWeeks } from "date-fns";
import PermissionGate from "../../../core/layouts/permission-gate";
import {
  allUsersStateAction,
  getAllUsersState,
  UserRole,
} from "../../../redux/features/user-slice";
import { mgTeam } from "../../../core/utils/constants";
import { AppDispatch } from "../../../redux/store";
import { setBreadcrumbState } from "../../../redux/features/common-slice";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import {
  calAbsentTime,
  isSelectMultiValue,
  isSelectSingleValue,
} from "../../../core/utils";
import Button from "../../../components/button";
import {
  ExportDReportsResponse,
  ExportFormat,
  ExportPeriod,
  ExportReportsRequest,
  ExportReportsResponse,
  ExportType,
} from "../../../redux/features/data-export.slice";
import StyledLabel from "../../../components/styled-label";
import DatePicker from "../../../components/date-picker";
import {
  adjustDateRange,
  dateFormatter,
  setTimeForDate,
} from "../../../core/utils/datetime-utils";
import { getUserState } from "../../../redux/features/auth-slice";
import { onPost } from "../../../core/api-service";
import { exportToExcel } from "../../../core/utils/excel-helpers";
import axiosInstance from "../../../core/api-service/axios";
import {
  AbsentDate,
  amisHeaders,
  amisUrl,
  AttendanceRecord,
} from "../../../redux/features/misa-slice";
import SwitchTab from "../../../components/switch-tab";
import {
  CommonStatus,
  getGroupTeamState,
  groupTeamStateAction,
} from "../../../redux/features/other-slice";

export default function ExportReportsPage() {
  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector(getUserState).data;
  const usersState = useSelector(getAllUsersState);
  const groupTeamsState = useSelector(getGroupTeamState);

  const [period, setPeriod] = useState<ExportPeriod>(ExportPeriod.Custom);
  const [exportByTeams, setExportByTeams] = useState(false);

  const fetchAllUsers = useCallback(async () => {
    if (
      user &&
      [
        UserRole.Admin,
        UserRole.Supervisor,
        UserRole.Manager,
        UserRole.Leader,
      ].includes(user.role)
    ) {
      await dispatch(
        allUsersStateAction({
          teamIds: user.team.id === mgTeam.id ? [] : [user.team.id],
          roles: [UserRole.Admin, UserRole.Supervisor].includes(user.role)
            ? []
            : user.role === UserRole.Manager
            ? [UserRole.Leader, UserRole.Contributor, UserRole.Member]
            : [UserRole.Contributor, UserRole.Member],
        })
      );
      if (user.role === UserRole.Admin && user.team.id === mgTeam.id) {
        await dispatch(
          groupTeamStateAction({
            status: [CommonStatus.Active, CommonStatus.Inactive],
          })
        );
      }
    }
  }, [dispatch, user]);

  const getAbsentDates = async (
    userMisaCode: string,
    start: Date,
    end: Date
  ): Promise<AbsentDate[]> => {
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const response = await axiosInstance.post(
        amisUrl,
        {
          Filter: null,
          CustomFilter: null,
          QuickSearch: {},
          CustomParam: {
            SubSystemCode: "Attendance",
            FromDate: dateFormatter(start, "yyyy-MM-dd"),
            ToDate: dateFormatter(end, "yyyy-MM-dd"),
          },
        },
        {
          headers: amisHeaders,
        }
      );
      // const data = response.data.Data.PageData;
      const attendances = response.data.Data.PageData.filter(
        (e: AttendanceRecord) =>
          e.EmployeeCode === userMisaCode && e.Status === 2
      );
      const dates = [];
      for (let dt = startDate; dt <= endDate; dt.setDate(dt.getDate() + 1)) {
        dates.push(new Date(dt));
      }

      const absentDates: AbsentDate[] = [];
      dates.map((date: Date) => {
        let absentTime = 0;
        let attAbsentFrom = new Date();
        let attAbsentTo = new Date();
        let attType = "";
        let attReason = "";
        const currentDate = date;
        const breakStart: Date = setTimeForDate(currentDate, "12:00");
        const breakEnd: Date = setTimeForDate(currentDate, "13:00");

        if (user) {
          for (const workingTime of user.workingTimes) {
            const workStart = setTimeForDate(
              currentDate,
              workingTime.startTime
            );
            const workEnd = setTimeForDate(currentDate, workingTime.endTime);

            for (const attendance of attendances) {
              const absentFrom = new Date(attendance.FromDate);
              absentFrom.setSeconds(0, 0);
              const absentTo = new Date(attendance.ToDate);
              absentTo.setSeconds(0, 0);

              const absentHour = calAbsentTime(
                absentFrom,
                absentTo,
                workStart,
                workEnd,
                breakStart,
                breakEnd
              );

              if (absentHour > absentTime) {
                attType = attendance.AttendanceTypeName_EN;
                attReason = attendance.Reason;
                absentTime = absentHour;
                attAbsentFrom = absentFrom;
                attAbsentTo = absentTo;
              }
            }
          }
        }
        if (
          absentTime > 0 &&
          absentDates.findIndex(
            (ad) =>
              ad.date === dateFormatter(date, "yyyy-MM-dd") &&
              ad.total === absentTime
          ) === -1
        ) {
          absentDates.push({
            date: dateFormatter(date, "yyyy-MM-dd"),
            from: attAbsentFrom,
            to: attAbsentTo,
            total: absentTime,
            type: attType,
            reason: attReason,
          });
        }
      });
      return absentDates;
    } catch (error: any) {
      return [];
    }
  };

  const exportData = async (values: ExportReportsRequest) => {
    let selectedUsers;
    if (exportByTeams) {
      selectedUsers = usersState.data.filter((user) => {
        return (
          user.groupTeam?.id && values.groupTeamIds.includes(user.groupTeam?.id)
        );
      });
    } else {
      selectedUsers = values.userIds
        .map((userId) => usersState.data.find((e) => e.id === userId))
        .filter(Boolean);
    }

    const checkUser =
      user && [UserRole.Contributor, UserRole.Member].includes(user.role);
    const validUsers = checkUser ? [user] : selectedUsers;
    const userIds = validUsers.map((selectedUser) => selectedUser?.id);
    // const absentDatesPromises = validUsers.map((selectedUser) =>
    //   getAbsentDates(selectedUser?.misaCode ?? "", values.start, values.end)
    // );

    try {
      // const absentDatesArray = await Promise.all(absentDatesPromises);
      const response = await onPost("/export/common", {
        userIds,
        start: dateFormatter(values.start, "yyyy-MM-dd"),
        end: dateFormatter(values.end, "yyyy-MM-dd"),
        types: values.types,
      });
      if (response.data) {
        console.log(response.data);
        exportToExcel(
          response.data as ExportReportsResponse,
          [], //cunrently hasn't done anything with absentDates
          `Weekly Reports ${dateFormatter(
            values.start,
            "dd-MM-yyyy"
          )} to ${dateFormatter(values.end, "dd-MM-yyyy")}`
        );
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  useEffect(() => {
    fetchAllUsers();
    dispatch(
      setBreadcrumbState([
        { id: "data", path: null, title: "Data" },
        {
          id: "exportReports",
          path: null,
          title: "Export Reports",
        },
      ])
    );
  }, [fetchAllUsers]);

  return (
    <PermissionGate categories={[]} roles={[]}>
      <Helmet title="Export Reports" />
      <Formik
        initialValues={
          {
            types: [],
            // format: ExportFormat.Excel,
            userIds:
              user &&
              [UserRole.Contributor, UserRole.Member].includes(user.role)
                ? [user.id]
                : [],
            start: new Date(),
            end: addWeeks(new Date(), 1),
            groupTeamIds: [],
          } as ExportReportsRequest
        }
        validationSchema={Yup.object().shape({
          types: Yup.array()
            .min(1, "You must select at least one type")
            .required("This field is required"),
          userIds: exportByTeams
            ? Yup.array().notRequired()
            : Yup.array<String>()
                .required("User(s) is required")
                .min(1, "You must select at least one User"),
          groupTeamIds: exportByTeams
            ? Yup.array<String>()
                .required("Team(s) is required")
                .min(1, "You must select at least one Team")
            : Yup.array().notRequired(),
        })}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
          exportData(values);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
          dirty,
        }) => (
          <Form
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            className="h-full overflow-y-auto"
          >
            <div
              className="h-full grid grid-cols-5 gap-4"
              style={{
                height: "calc(100% - 40px)",
              }}
            >
              <div className="col-span-4 grid grid-cols-2 gap-4">
                <div className="col-span-1 flex flex-col space-y-4">
                  <div>
                    <StyledLabel label="Type" />
                    <StyledSelect
                      loading={false}
                      options={Object.values(ExportType).map((e) => ({
                        value: e,
                        label: e.capitalizeWords(),
                      }))}
                      isClearable={false}
                      menuPosition="fixed"
                      value={{
                        value: "",
                        label:
                          values.types.length > 0
                            ? values.types
                                .map((e) => e.capitalizeWords())
                                .join(", ")
                            : "Select...",
                      }}
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          if (
                            values.types.findIndex((e) => e === value.value) !==
                            -1
                          ) {
                            setValues({
                              ...values,
                              types: values.types.filter(
                                (r) => r !== value.value
                              ),
                            });
                          } else {
                            setValues({
                              ...values,
                              types: [
                                ...values.types,
                                value.value as ExportType,
                              ],
                            });
                          }
                        }
                      }}
                      exOption={(value: OptionValue) => {
                        return {
                          background:
                            values.types.findIndex((e) => e === value.value) !==
                            -1
                              ? "#f1f5f9"
                              : undefined,
                        };
                      }}
                    />
                  </div>
                  <div>
                    <StyledLabel label="Format" />
                    <StyledSelect
                      loading={false}
                      options={Object.values(ExportFormat).map((e) => ({
                        value: e,
                        label: e.capitalize(),
                      }))}
                      isClearable={false}
                      menuPosition="fixed"
                      value={{
                        value: ExportFormat.Excel,
                        label: ExportFormat.Excel.capitalize(),
                      }}
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        // if (value && isSelectSingleValue(value)) {
                        //   setValues({
                        //     ...values,
                        //     format: value.value as ExportFormat,
                        //   });
                        // }
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-1 flex flex-col space-y-4">
                  <div>
                    <StyledLabel label="Period" />
                    <StyledSelect
                      loading={false}
                      options={Object.values(ExportPeriod).map(
                        (e: ExportPeriod) => ({
                          value: e,
                          label: e.capitalizeWords(),
                        })
                      )}
                      isClearable={false}
                      menuPosition="fixed"
                      value={{
                        value: period,
                        label: period.capitalizeWords(),
                      }}
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          const period = value.value as ExportPeriod;
                          setPeriod(period);
                          const newPeriod = adjustDateRange(period);

                          if (newPeriod.startDate && newPeriod.endDate) {
                            setValues({
                              ...values,
                              start: newPeriod.startDate,
                              end: newPeriod.endDate,
                            });
                          }
                        }
                      }}
                    />
                  </div>
                  <div>
                    <StyledLabel label="Start Date" />
                    <DatePicker
                      key="startPicker"
                      defaultValue={values.start}
                      readOnly={period !== ExportPeriod.Custom}
                      onChanged={(value) =>
                        setValues({ ...values, start: value })
                      }
                    />
                  </div>
                  <div>
                    <StyledLabel label="End Date" />
                    <DatePicker
                      key="endPicker"
                      defaultValue={values.end}
                      readOnly={period !== ExportPeriod.Custom}
                      onChanged={(value) =>
                        setValues({ ...values, end: value })
                      }
                    />
                  </div>
                  {user &&
                    ![UserRole.Contributor, UserRole.Member].includes(
                      user.role
                    ) && (
                      <div>
                        <div className="flex items-center space-x-4 pb-1">
                          <label className="text-xs text-text-title mb-1">
                            Export by:
                          </label>
                          <SwitchTab
                            options={["Teams", "Users"]}
                            tab={exportByTeams ? "Teams" : "Users"}
                            setTab={(value: string) =>
                              setExportByTeams(value === "Teams")
                            }
                          />
                        </div>
                        {!exportByTeams ? (
                          <StyledSelect
                            isCloseMenuOnSelect={false}
                            loading={usersState.loading}
                            options={usersState.data.map((e) => ({
                              value: e.id,
                              label: `${e.name} - ${e.email}`,
                            }))}
                            isClearable={false}
                            multiple={true}
                            menuPosition="fixed"
                            value={
                              values.userIds
                                .map((userId) => {
                                  const user = usersState.data.find(
                                    (e) => e.id === userId
                                  );
                                  return user
                                    ? {
                                        value: user.id,
                                        label: `${user.name}`,
                                      }
                                    : undefined;
                                })
                                .filter(Boolean) as OptionValue[]
                            }
                            onChange={(
                              value:
                                | SingleValue<OptionValue>
                                | MultiValue<OptionValue>
                            ) => {
                              if (value && isSelectMultiValue(value)) {
                                const selectedUserIds = value.map(
                                  (v) => v.value as string
                                );
                                setValues({
                                  ...values,
                                  userIds: selectedUserIds,
                                });
                              } else {
                                setValues({
                                  ...values,
                                  userIds: [],
                                });
                              }
                            }}
                          />
                        ) : (
                          <StyledSelect
                            isCloseMenuOnSelect={false}
                            loading={groupTeamsState.loading}
                            options={groupTeamsState.data.map((e) => ({
                              value: e.id,
                              label: `${e.name}`,
                            }))}
                            isClearable={false}
                            multiple={true}
                            menuPosition="fixed"
                            value={
                              values.groupTeamIds
                                .map((groupTeamId) => {
                                  const groupTeam = groupTeamsState.data.find(
                                    (e) => e.id === groupTeamId
                                  );
                                  return groupTeam
                                    ? {
                                        value: groupTeam.id,
                                        label: `${groupTeam.name}`,
                                      }
                                    : undefined;
                                })
                                .filter(Boolean) as OptionValue[]
                            }
                            onChange={(
                              value:
                                | SingleValue<OptionValue>
                                | MultiValue<OptionValue>
                            ) => {
                              if (value && isSelectMultiValue(value)) {
                                const selectedGroupTeamIds = value.map(
                                  (v) => v.value as string
                                );
                                setValues({
                                  ...values,
                                  groupTeamIds: selectedGroupTeamIds,
                                });
                              } else {
                                setValues({
                                  ...values,
                                  groupTeamIds: [],
                                });
                              }
                            }}
                          />
                        )}
                      </div>
                    )}
                </div>
              </div>
              {/* <div className="col-span-2 bg-background-default shadow rounded-lg"></div> */}
            </div>
            <div className="flex items-center space-x-4">
              <Button
                label="Export"
                type="submit"
                disabled={
                  Array.isArray(errors) ||
                  Object.values(errors).toString() != ""
                }
              />
            </div>
          </Form>
        )}
      </Formik>
    </PermissionGate>
  );
}
